import {
    FlagOutlined,
    UnorderedListOutlined,
    ClockCircleOutlined,
    LoadingOutlined,
    FireOutlined,
} from '@ant-design/icons';
import { Badge, Flex, Spin, Switch, Tabs, Typography } from 'antd';
import InboxV2CardList from './listing/inboxV2CardList';
import InboxV2EmailList from './listing/inboxV2EmailList';
import styles from '../inboxPageV2.module.less';
import useInboxV2 from '../useInboxV2';
import { TAB_LOADER_TEST_ID } from '../constants';
import InboxV2ListSelectBox from './listing/inboxV2ListSelectBox';
import { SEARCH_CONTEXTS, SearchContext } from '../context/types';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { URGENT_LEAD_CARD, REMINDER_CARD } from './card/types';
import { InboxMessageType } from '../../inbox/data/types';
import { INBOX_UNREPLIED_TOGGLE_TRACKING_ID } from '@copilot/common/tracking/userpilotEventConsts';
import { AiSwitch } from '@copilot/common/components/ai/aiSwitch/aiSwitch';
import { useFeatureFlags } from '@copilot/common/hooks/useFeatureFlags/useFeatureFlags';

/**
 * Tab section for InboxV2, each tab has it's own listing strategy and implicit filters (eg Reminders vs New Messages)
 */
export default function InboxV2TabsSection() {
    const {
        messages,
        hasFilter,
        numUnreadMessages,
        numReminders,
        numUrgentMessages,
        searchContext,
        onSearchTabChange,
        isTabLoading,
        reminderFilterOptions,
        getRemindersContextFilter,
        onRemindersContextFilterChange,
        onAllMessagesContextFilterChange,
        hasMore,
        isLoading,
        onLoadMore,
        onViewConversation,
        onDeleteMessages,
        onMarkReminderAsComplete,
        onDismissThreads,
        isReplyRecommendationsEnabled,
        onReplyRecommendationsEnabledChange,
        isLoadingReplyRecommendation,
    } = useInboxV2();
    const {
        ALL_MESSAGES_CONTEXT,
        UNREAD_MESSAGES_CONTEXT,
        REMINDERS_CONTEXT,
        URGENT_LEADS_CONTEXT,
    } = SEARCH_CONTEXTS;

    const { isReplyRecommendationsEnabled: isReplyRecommendationsEnabledFeatureFlag } =
        useFeatureFlags();

    function getTabIcon(key: SearchContext) {
        if (isTabLoading(key)) {
            return (
                <Spin
                    data-testid={TAB_LOADER_TEST_ID}
                    indicator={<LoadingOutlined className={styles.tabSpinner} />}
                />
            );
        }
        switch (key) {
            case ALL_MESSAGES_CONTEXT:
                return <UnorderedListOutlined />;
            case UNREAD_MESSAGES_CONTEXT:
                return <FlagOutlined />;
            case REMINDERS_CONTEXT:
                return <ClockCircleOutlined />;
            case URGENT_LEADS_CONTEXT:
                return <FireOutlined />;
            default:
                return null;
        }
    }

    /**
     * Calcs what tabs to show based on feature toggle config, etc
     */
    function getTabs() {
        const baseTabs = [
            {
                key: ALL_MESSAGES_CONTEXT,
                label: (
                    <Flex gap={8} align="center">
                        <div>{getTabIcon(ALL_MESSAGES_CONTEXT)}</div>
                        All
                        <Badge
                            count={numUnreadMessages}
                            className={styles.tabBadge}
                            overflowCount={999}
                        />
                    </Flex>
                ),
                children: (
                    <InboxV2EmailList
                        messages={messages}
                        hasFilter={hasFilter}
                        isLoading={isLoading}
                        onReload={() => {}}
                        canLoadMore={hasMore}
                        isLoadingMore={false}
                        onLoadMore={onLoadMore}
                        onViewConversation={onViewConversation}
                        isSelectable={true}
                        extraHeaderContent={
                            <Flex gap={10} align="center">
                                <Switch
                                    onChange={onAllMessagesContextFilterChange}
                                    data-tracking-id={INBOX_UNREPLIED_TOGGLE_TRACKING_ID}
                                />
                                <Typography.Text className={styles.listSwitchLabel}>
                                    View unread only ({numUnreadMessages})
                                </Typography.Text>
                            </Flex>
                        }
                        onRemove={(msgs: InboxMessageType[]) =>
                            onDeleteMessages(msgs.map((m) => m.threadId))
                        }
                    />
                ),
            },
            {
                key: REMINDERS_CONTEXT,
                label: (
                    <Flex gap={8} align="center">
                        <div>{getTabIcon(REMINDERS_CONTEXT)}</div>
                        Reminders
                        <Badge
                            count={numReminders}
                            className={styles.tabBadge}
                            overflowCount={999}
                        />
                    </Flex>
                ),
                children: (
                    <InboxV2CardList
                        messages={messages.filter((m) => !isNil(m.reminder))}
                        hasFilter={hasFilter}
                        extraHeaderContent={
                            <InboxV2ListSelectBox
                                options={reminderFilterOptions}
                                defaultValue={getRemindersContextFilter()}
                                onChange={onRemindersContextFilterChange}
                            />
                        }
                        isLoading={isLoading}
                        onReload={() => {}}
                        canLoadMore={hasMore}
                        isLoadingMore={false}
                        onLoadMore={onLoadMore}
                        onViewConversation={onViewConversation}
                        onRemove={onMarkReminderAsComplete}
                        cardType={REMINDER_CARD}
                    />
                ),
            },
        ];

        return [
            {
                key: URGENT_LEADS_CONTEXT,
                label: (
                    <Flex gap={8}>
                        <div>{getTabIcon(URGENT_LEADS_CONTEXT)}</div>
                        Unreplied
                        <Badge count={numUrgentMessages} className={styles.tabBadge} />
                    </Flex>
                ),
                children: (
                    <InboxV2CardList
                        messages={messages}
                        hasFilter={hasFilter}
                        showIntents={true}
                        isLoading={isLoading}
                        onReload={() => {}}
                        canLoadMore={hasMore}
                        isLoadingMore={isLoading && !isEmpty(messages)}
                        onLoadMore={onLoadMore}
                        onViewConversation={onViewConversation}
                        onRemove={(threads) => onDismissThreads(threads.map((thread) => thread.id))}
                        cardType={URGENT_LEAD_CARD}
                        extraHeaderContent={
                            isReplyRecommendationsEnabledFeatureFlag && (
                                <Flex
                                    gap={8}
                                    align="center"
                                    justify="flex-end"
                                    style={{ width: '100%' }}
                                >
                                    <span>Show recommendations</span>
                                    <AiSwitch
                                        checked={isReplyRecommendationsEnabled}
                                        onChange={(isEnabled) =>
                                            onReplyRecommendationsEnabledChange(isEnabled)
                                        }
                                        defaultChecked={isReplyRecommendationsEnabled}
                                        disabled={isLoadingReplyRecommendation}
                                        loading={isLoadingReplyRecommendation}
                                        size="small"
                                    />
                                </Flex>
                            )
                        }
                    />
                ),
            },
            ...baseTabs,
        ];
    }

    return (
        <Tabs
            defaultActiveKey={searchContext}
            className={styles.tabsSection}
            onChange={onSearchTabChange}
            //prevents us from rendering non-visible tabs
            destroyInactiveTabPane
            items={getTabs()}
        />
    );
}
